import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import api from '@/lib/api';
import { Image, Monitor, XCircle, Upload, Save, Eye, Paintbrush, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BrandingSettings = () => {
  const [form, setForm] = useState({
    title: 'Baobab Connect',
    subtitle: 'Falcon',
    background_url: '',
    background_type: 'image',
    logo_url: '',
    primary_color: '#059669',
    secondary_color: '#f59e0b',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState({ logo: false, background: false });
  const logoInputRef = useRef(null);
  const bgInputRef = useRef(null);

  useEffect(() => {
    const fetchBranding = async () => {
      try {
        const resp = await api.get('/branding/login');
        setForm(prev => ({ ...prev, ...resp.data }));
      } catch {}
      finally { setLoading(false); }
    };
    fetchBranding();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/branding/login', form);
      toast.success('Personnalisation sauvegardee');
    } catch { toast.error('Erreur lors de la sauvegarde'); }
    finally { setSaving(false); }
  };

  const handleUpload = async (type, file) => {
    if (!file) return;
    const maxSize = type === 'logo' ? 5 * 1024 * 1024 : 10 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error(`Fichier trop volumineux (max ${maxSize / (1024 * 1024)}MB)`);
      return;
    }
    setUploading(prev => ({ ...prev, [type]: true }));
    try {
      const formData = new FormData();
      formData.append('file', file);
      const resp = await api.post(`/branding/upload/${type}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const url = `${API_URL}${resp.data.url}`;
      if (type === 'logo') {
        setForm(prev => ({ ...prev, logo_url: url }));
      } else {
        setForm(prev => ({ ...prev, background_url: url }));
      }
      toast.success(`${type === 'logo' ? 'Logo' : 'Image de fond'} uploade`);
    } catch (err) {
      toast.error('Erreur lors de l\'upload');
      console.error(err);
    } finally {
      setUploading(prev => ({ ...prev, [type]: false }));
    }
  };

  if (loading) return <div className="text-center py-8 text-gray-400">Chargement...</div>;

  return (
    <div className="space-y-6" data-testid="branding-settings">
      {/* Header */}
      <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-start gap-3">
        <Paintbrush className="h-5 w-5 text-emerald-600 mt-0.5" />
        <div>
          <h3 className="font-semibold text-emerald-900">Personnalisation de la Marque</h3>
          <p className="text-sm text-emerald-700">Personnalisez l'apparence de votre plateforme et de la page de connexion.</p>
        </div>
      </div>

      {/* Logo */}
      <Card>
        <CardContent className="p-5 space-y-4">
          <div className="flex items-center gap-2 mb-1">
            <Image className="h-5 w-5 text-emerald-500" />
            <h4 className="font-semibold text-gray-900">Logo de l'entreprise</h4>
          </div>
          <p className="text-sm text-gray-500">Ce logo sera affiche sur la page de connexion et dans la barre laterale.</p>

          <div className="flex items-start gap-4">
            <div className="relative">
              {form.logo_url ? (
                <div className="w-24 h-24 rounded-lg border-2 border-dashed border-gray-200 overflow-hidden bg-white flex items-center justify-center">
                  <img src={form.logo_url} alt="Logo" className="max-w-full max-h-full object-contain" />
                  <button
                    onClick={() => setForm(prev => ({ ...prev, logo_url: '' }))}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full h-5 w-5 flex items-center justify-center hover:bg-red-600"
                    data-testid="remove-logo-btn"
                  >
                    <XCircle className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <div className="w-24 h-24 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center bg-gray-50">
                  <Image className="h-8 w-8 text-gray-300" />
                </div>
              )}
            </div>
            <div className="flex-1 space-y-2">
              <input type="file" ref={logoInputRef} className="hidden" accept="image/*"
                onChange={(e) => handleUpload('logo', e.target.files?.[0])} />
              <Button
                variant="outline"
                onClick={() => logoInputRef.current?.click()}
                disabled={uploading.logo}
                className="text-emerald-600 border-emerald-200 hover:bg-emerald-50"
                data-testid="upload-logo-btn"
              >
                {uploading.logo ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
                {uploading.logo ? 'Upload en cours...' : 'Choisir un logo'}
              </Button>
              <p className="text-xs text-gray-400">Formats: PNG, JPG, SVG. Max 5MB.</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Background */}
      <Card>
        <CardContent className="p-5 space-y-4">
          <div className="flex items-center gap-2 mb-1">
            <Monitor className="h-5 w-5 text-emerald-500" />
            <h4 className="font-semibold text-gray-900">Fond de la page de connexion</h4>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => setForm(prev => ({ ...prev, background_type: 'gradient' }))}
              className={`flex items-center gap-3 p-4 rounded-lg border-2 transition-all ${
                form.background_type === 'gradient' ? 'border-emerald-500 bg-emerald-50/50' : 'border-gray-200 hover:border-gray-300'
              }`}
              data-testid="bg-gradient-option"
            >
              <div className="w-10 h-10 rounded-lg flex-shrink-0" style={{ background: `linear-gradient(135deg, ${form.primary_color}, ${form.secondary_color})` }} />
              <div className="text-left">
                <p className="font-medium text-sm text-gray-900">Degrade de couleur</p>
                <p className="text-xs text-gray-500">Utiliser la couleur primaire</p>
              </div>
            </button>
            <button
              onClick={() => setForm(prev => ({ ...prev, background_type: 'image' }))}
              className={`flex items-center gap-3 p-4 rounded-lg border-2 transition-all ${
                form.background_type === 'image' ? 'border-emerald-500 bg-emerald-50/50' : 'border-gray-200 hover:border-gray-300'
              }`}
              data-testid="bg-image-option"
            >
              <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                <Image className="h-5 w-5 text-gray-400" />
              </div>
              <div className="text-left">
                <p className="font-medium text-sm text-gray-900">Image personnalisee</p>
                <p className="text-xs text-gray-500">Uploader une image de fond</p>
              </div>
            </button>
          </div>

          {form.background_type === 'image' && (
            <div className="space-y-3 pt-2">
              {form.background_url && (
                <div className="relative inline-block">
                  <div className="w-48 h-28 rounded-lg overflow-hidden border">
                    <img src={form.background_url} alt="Fond" className="w-full h-full object-cover" />
                  </div>
                  <button
                    onClick={() => setForm(prev => ({ ...prev, background_url: '' }))}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full h-5 w-5 flex items-center justify-center hover:bg-red-600"
                    data-testid="remove-bg-btn"
                  >
                    <XCircle className="h-4 w-4" />
                  </button>
                </div>
              )}
              <div>
                <input type="file" ref={bgInputRef} className="hidden" accept="image/*"
                  onChange={(e) => handleUpload('background', e.target.files?.[0])} />
                <Button
                  variant="outline"
                  onClick={() => bgInputRef.current?.click()}
                  disabled={uploading.background}
                  className="text-emerald-600 border-emerald-200 hover:bg-emerald-50"
                  data-testid="upload-bg-btn"
                >
                  {uploading.background ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
                  {uploading.background ? 'Upload en cours...' : 'Choisir une image'}
                </Button>
                <p className="text-xs text-gray-400 mt-1">Recommande: 1920x1080px. Formats: PNG, JPG. Max 10MB.</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Text & Colors */}
      <Card>
        <CardContent className="p-5 space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="space-y-2">
              <Label className="text-gray-700">Nom de l'entreprise</Label>
              <Input value={form.title} onChange={(e) => setForm(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Baobab Connect" data-testid="branding-title-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-700">Sous-titre</Label>
              <Input value={form.subtitle} onChange={(e) => setForm(prev => ({ ...prev, subtitle: e.target.value }))}
                placeholder="Falcon" data-testid="branding-subtitle-input" />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="space-y-2">
              <Label className="text-gray-700">Couleur primaire</Label>
              <div className="flex items-center gap-3">
                <input type="color" value={form.primary_color} onChange={(e) => setForm(prev => ({ ...prev, primary_color: e.target.value }))}
                  className="h-10 w-16 rounded-lg border cursor-pointer" data-testid="primary-color-input" />
                <Input value={form.primary_color} onChange={(e) => setForm(prev => ({ ...prev, primary_color: e.target.value }))} className="w-28" />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-gray-700">Couleur secondaire</Label>
              <div className="flex items-center gap-3">
                <input type="color" value={form.secondary_color} onChange={(e) => setForm(prev => ({ ...prev, secondary_color: e.target.value }))}
                  className="h-10 w-16 rounded-lg border cursor-pointer" data-testid="secondary-color-input" />
                <Input value={form.secondary_color} onChange={(e) => setForm(prev => ({ ...prev, secondary_color: e.target.value }))} className="w-28" />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Preview */}
      <Card>
        <CardContent className="p-5">
          <Label className="text-gray-700 mb-3 block">Apercu de la page de connexion</Label>
          <div className="rounded-xl overflow-hidden border shadow-sm">
            <div className="relative h-64 flex items-center justify-center" style={{
              backgroundImage: form.background_type === 'image' && form.background_url ? `url(${form.background_url})` : undefined,
              backgroundSize: 'cover', backgroundPosition: 'center',
              background: form.background_type === 'gradient' ? `linear-gradient(135deg, ${form.primary_color}, ${form.secondary_color})` : undefined,
              backgroundColor: form.background_type === 'image' && !form.background_url ? '#1f2937' : undefined,
            }}>
              {form.background_type === 'image' && form.background_url && <div className="absolute inset-0 bg-black/50" />}
              <div className="relative z-10 bg-white/95 rounded-xl p-6 text-center shadow-lg w-52">
                {form.logo_url ? (
                  <img src={form.logo_url} alt="Logo" className="h-10 mx-auto mb-2 object-contain" />
                ) : (
                  <svg width="36" height="36" viewBox="0 0 64 64" fill="none" className="mx-auto mb-2">
                    <path d="M32 4C28 4 20 10 16 18C12 26 12 32 14 36C16 40 18 42 22 44L20 52L24 50L26 54L30 50L32 56L34 50L38 54L40 50L44 52L42 44C46 42 48 40 50 36C52 32 52 26 48 18C44 10 36 4 32 4Z" fill={form.primary_color} opacity="0.15" stroke={form.primary_color} strokeWidth="2" strokeLinejoin="round"/>
                    <circle cx="26" cy="24" r="2" fill={form.primary_color}/><circle cx="38" cy="24" r="2" fill={form.primary_color}/>
                    <path d="M29 30L32 34L35 30" stroke={form.primary_color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
                <p className="font-bold text-sm text-gray-900">{form.title || 'Baobab Connect'}</p>
                <p className="text-xs text-gray-500 mb-3">{form.subtitle || 'Falcon'}</p>
                <div className="space-y-1.5">
                  <div className="h-7 rounded-md border border-gray-200 bg-gray-50 flex items-center px-2 text-[10px] text-gray-400">votre@email.com</div>
                  <div className="h-7 rounded-md text-white text-[10px] flex items-center justify-center font-medium" style={{ backgroundColor: form.primary_color }}>Se connecter</div>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex items-center gap-3">
        <Button onClick={handleSave} disabled={saving} data-testid="branding-save-btn" style={{ backgroundColor: form.primary_color }}>
          <Save className="h-4 w-4 mr-2" />
          {saving ? 'Sauvegarde...' : 'Sauvegarder les modifications'}
        </Button>
        <Button variant="outline" onClick={() => window.open('/login', '_blank')}>
          <Eye className="h-4 w-4 mr-2" /> Voir la page de connexion
        </Button>
      </div>
    </div>
  );
};

export default BrandingSettings;
